/**
 * SupplierAccordion Component
 * 협력사별 아코디언 행 (테이블 포함)
 */
import React from 'react';
import { SupplierWorkStatus, RiderWorkStatusItem } from '../../types/riderWorkStatus.types';
import SortIcon from '@/shared/components/dataDisplay/SortIcon';
import { formatDateToMonthDay } from '../../utils/dateFormatters';
import { SortKey, SortDirection } from '../../hooks/useSortableTable';

interface SupplierAccordionProps {
    supplier: SupplierWorkStatus;
    isExpanded: boolean;
    onToggle: (suppId: string) => void;
    getFilteredAndSortedRiders: (riders: RiderWorkStatusItem[]) => RiderWorkStatusItem[];
    sortKey: SortKey | null;
    sortDirection: SortDirection;
    onSort: (key: SortKey) => void;
}

/**
 * 날짜 목록 추출 (모든 라이더의 workStatusByDate에서 날짜 수집)
 */
const getDateList = (supplier: SupplierWorkStatus): string[] => {
    const dateSet = new Set<string>();
    supplier.riders.forEach((rider: RiderWorkStatusItem) => {
        Object.keys(rider.workStatusByDate).forEach((date) => {
            dateSet.add(date);
        });
    });
    return Array.from(dateSet).sort();
};

/**
 * 근무 여부 표시
 */
const renderWorkStatus = (isWorking: boolean | undefined): React.ReactNode => {
    if (isWorking === undefined) {
        return <span className="work-status-badge no-data">-</span>;
    }
    return (
        <span className={`work-status-badge ${isWorking ? 'working' : 'not-working'}`}>
            {isWorking ? '근무' : '미근무'}
        </span>
    );
};

const SupplierAccordion: React.FC<SupplierAccordionProps> = ({
                                                                 supplier,
                                                                 isExpanded,
                                                                 onToggle,
                                                                 getFilteredAndSortedRiders,
                                                                 sortKey,
                                                                 sortDirection,
                                                                 onSort,
                                                             }) => {
    const dateList = getDateList(supplier);

    return (
        <div className="supplier-accordion-item">
            {/* 협력사 헤더 */}
            <div className="supplier-header" onClick={() => onToggle(supplier.suppId)}>
                <div className="supplier-header-content">
                    <span className="supplier-name">{supplier.suppName}</span>
                    <span className="supplier-info">
                        ({supplier.suppId}) · 라이더 {supplier.riders.length}명
                    </span>
                </div>
                <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
            </div>

            {/* 라이더 목록 (아코디언 내용) */}
            {isExpanded && (
                <div className="supplier-content">
                    <div className="table-responsive">
                        <table className="work-status-table">
                            <thead>
                            <tr>
                                <th
                                    className="sortable-header"
                                    onClick={() => onSort('riderId')}
                                >
                                    라이더 ID
                                    <SortIcon direction={sortKey === 'riderId' ? sortDirection : null} />
                                </th>
                                <th
                                    className="sortable-header"
                                    onClick={() => onSort('riderName')}
                                >
                                    라이더명
                                    <SortIcon direction={sortKey === 'riderName' ? sortDirection : null} />
                                </th>
                                <th>배달처리비 합계</th>
                                <th
                                    className="sortable-header"
                                    onClick={() => onSort('totalWorkDays')}
                                >
                                    총 근무일수
                                    <SortIcon direction={sortKey === 'totalWorkDays' ? sortDirection : null} />
                                </th>
                                <th
                                    className="sortable-header"
                                    onClick={() => onSort('totalDeliveryCnt')}
                                >
                                    총 배달 건수
                                    <SortIcon direction={sortKey === 'totalDeliveryCnt' ? sortDirection : null} />
                                </th>
                                {dateList.map((date) => (
                                    <th key={date}>{formatDateToMonthDay(date)}</th>
                                ))}
                            </tr>
                            </thead>
                            <tbody>
                            {getFilteredAndSortedRiders(supplier.riders).map((rider) => (
                                <tr key={rider.riderId}>
                                    <td>{rider.riderId}</td>
                                    <td>{rider.riderName}</td>
                                    <td className="delivery-fee-total">
                                        {rider.deliveryFeeTotal !== undefined
                                            ? `${rider.deliveryFeeTotal.toLocaleString()}원`
                                            : '-'}
                                    </td>
                                    <td className="total-work-days">{rider.totalWorkDays}일</td>
                                    <td className="total-delivery-cnt">
                                        {rider.totalDeliveryCnt !== undefined
                                            ? rider.totalDeliveryCnt.toLocaleString()
                                            : '-'}
                                    </td>
                                    {dateList.map((date) => (
                                        <td key={date} className="work-status-cell">
                                            {renderWorkStatus(rider.workStatusByDate[date])}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SupplierAccordion;
